/**
 * Dlubal NBC 2020 client for Fast Rack (Canada).
 *
 * Fetches snow load zones (Ss, Sr) and wind pressure q1/50 (kPa) in a single
 * call to the combined CA Dlubal endpoint, then caches each value
 * separately in project custom_data under `dlubal_ca_snow` / `dlubal_ca_wind`.
 *
 * Pattern follows SolarPortDlubal.ts (UK single-call) rather than
 * K2Dlubal.ts (US two-call).
 */

import restClient from 'restClient'
import * as Sentry from '@sentry/react'
import { logAmplitudeEvent } from 'amplitude/amplitude'
import composeUrlWithQueryParams from 'util/fetch/composeUrlWithQueryParams'
import { generateUUID } from 'util/misc'
import { MountingInputsIntegrationData } from '../../types'
import { computeFastRackDesignSnow_kPa } from './FastRackComputations'

type DlubalCADataType = 'wind' | 'snow'

/**
 * Bump when the cache shape changes (fields added/removed) to force
 * re-fetch on projects with stale custom_data.
 */
export const DLUBAL_CA_SCHEMA_VERSION = 1

/** Max auto-fetch attempts from generateBom before giving up (manual Fetch is unaffected). */
export const DLUBAL_CA_MAX_AUTO_FETCH_ATTEMPTS = 1

const DLUBAL_CA_AUTO_FETCH_META_KEY = 'dlubal_ca_auto_fetch'

interface DlubalCAAutoFetchMeta {
  attempts: number
}

export class DlubalCAMissingLocationError extends Error {
  constructor() {
    super('DLUBAL_CA_MISSING_LOCATION')
    this.name = 'DlubalCAMissingLocationError'
  }
}

/** True when project form has usable coordinates for the CA Dlubal endpoint. */
export function isValidDlubalCAProjectCoordinates(lat: unknown, lon: unknown): boolean {
  const latNum = Number(lat)
  const lonNum = Number(lon)
  if (!Number.isFinite(latNum) || !Number.isFinite(lonNum)) return false
  if (latNum < -90 || latNum > 90 || lonNum < -180 || lonNum > 180) return false
  // Manage saves 0,0 when geocoding never ran — treat as unset, not Gulf of Guinea.
  if (latNum === 0 && lonNum === 0) return false
  return true
}

export function isDlubalCAMissingLocationError(error: unknown): boolean {
  return error instanceof DlubalCAMissingLocationError
}

export interface DlubalCACacheEntry {
  schema_version?: number
  snow_result?: number | null
  snow_zone_1?: number | null
  snow_zone_2?: number | null
  /** NBC design snow (kPa) at fetch-time slope — snapshot for Dlubal-populated field matching only. */
  snow_design_kPa?: number | null
  wind_result?: number | null
  wind_zone_2?: number | null
  success: boolean
}

interface DlubalCACacheData {
  value: number | null
  success: boolean
}

export interface DlubalCAResolvedValues {
  snow_kPa: number | null
  wind_q150_kPa: number | null
}

interface DlubalCACoverage {
  dlubal_snow: boolean
  dlubal_wind: boolean
  has_dlubal_coverage: boolean
  weather_station_fallback: boolean
}

function stringifyAnalyticsId(value: unknown): string | undefined {
  if (value === null || value === undefined || value === '') return undefined
  return String(value)
}

function getDlubalCAAnalyticsContext(): { org_id?: string; project_id?: string; system_id?: string } {
  const values = window.projectForm?.getState().values
  const org_id = stringifyAnalyticsId(values?.org_id)
  const project_id = stringifyAnalyticsId(values?.id)
  const system_id = stringifyAnalyticsId(window.editor?.selectedSystem?.uuid)

  return {
    ...(org_id ? { org_id } : {}),
    ...(project_id ? { project_id } : {}),
    ...(system_id ? { system_id } : {}),
  }
}

function trackDlubalCACoverage(coverage: DlubalCACoverage | undefined, fetchSuccess: boolean): void {
  logAmplitudeEvent('dlubal_ca_coverage_checked', {
    integration: 'fastRack',
    fetch_success: fetchSuccess,
    has_dlubal_coverage: coverage?.has_dlubal_coverage ?? false,
    dlubal_snow: coverage?.dlubal_snow ?? false,
    dlubal_wind: coverage?.dlubal_wind ?? false,
    weather_station_fallback: coverage?.weather_station_fallback ?? false,
    ...getDlubalCAAnalyticsContext(),
  })
}

function readCacheEntry(type: DlubalCADataType): DlubalCACacheEntry | null {
  if (!window.projectForm) return null
  const customData = window.projectForm.getState().values.custom_data || {}
  const cacheKey = type === 'snow' ? 'dlubal_ca_snow' : 'dlubal_ca_wind'
  const cached = customData[cacheKey]
  if (!cached || typeof cached !== 'object') return null
  if ((cached as DlubalCACacheEntry).schema_version !== DLUBAL_CA_SCHEMA_VERSION) return null
  return cached as DlubalCACacheEntry
}

function resolveSnowFromCache(entry: DlubalCACacheEntry | null, slopeDeg: number): number | null {
  if (!entry?.success) return null

  const Ss = entry.snow_zone_1 ?? entry.snow_result
  if (Ss === null || Ss === undefined) return null

  const Sr = entry.snow_zone_2 ?? 0
  return computeFastRackDesignSnow_kPa(Ss, Sr, slopeDeg)
}

function resolveWindFromCache(entry: DlubalCACacheEntry | null): number | null {
  if (!entry?.success) return null
  const q150 = entry.wind_zone_2 ?? entry.wind_result
  if (q150 === null || q150 === undefined) return null
  return q150
}

function mergeCacheEntry(
  existing: DlubalCACacheEntry | undefined,
  updates: Partial<DlubalCACacheEntry>
): DlubalCACacheEntry {
  return { ...existing, ...updates, success: updates.success ?? existing?.success ?? false }
}

function setDlubalCACache(type: DlubalCADataType, updates: Partial<DlubalCACacheEntry>): void {
  if (!window.projectForm) return

  const cacheKey = type === 'snow' ? 'dlubal_ca_snow' : 'dlubal_ca_wind'
  const customData = window.projectForm.getState().values.custom_data || {}
  const existing = customData[cacheKey] as DlubalCACacheEntry | undefined

  window.projectForm.change('custom_data', {
    ...customData,
    [cacheKey]: mergeCacheEntry(existing, updates),
  })
}

function setDlubalCACacheFailure(type: DlubalCADataType): void {
  const resultKey = type === 'snow' ? 'snow_result' : 'wind_result'
  setDlubalCACache(type, { [resultKey]: null, schema_version: DLUBAL_CA_SCHEMA_VERSION, success: false })
}

export function getDlubalCACacheEntry(type: DlubalCADataType): DlubalCACacheEntry | null {
  return readCacheEntry(type)
}

export function getDlubalCACache(type: DlubalCADataType, slopeDeg = 20): DlubalCACacheData | null {
  const entry = readCacheEntry(type)
  if (!entry) return null


  if (entry.success === false) {
    return { value: null, success: false }
  }

  const value = type === 'snow' ? resolveSnowFromCache(entry, slopeDeg) : resolveWindFromCache(entry)
  if (value === null) return null

  return { value, success: true }
}


/** Slope-independent NBC zone Ss or Sr from the CA snow cache. */
export function getDlubalCASnowZoneCache(zone: 'ss' | 'sr'): DlubalCACacheData | null {
  const entry = readCacheEntry('snow')
  if (!entry) return null
  if (entry.success === false) return { value: null, success: false }

  if (zone === 'ss') {
    const Ss = entry.snow_zone_1 ?? entry.snow_result ?? null
    if (Ss === null || Ss === undefined) return null
    return { value: Ss, success: true }
  }

  if (entry.snow_zone_1 === undefined && entry.snow_result === undefined && entry.snow_zone_2 === undefined) {
    return null
  }
  return { value: entry.snow_zone_2 ?? 0, success: true }
}

function hasResolvedSnowZonesCache(): boolean {
  const ss = getDlubalCASnowZoneCache('ss')
  const sr = getDlubalCASnowZoneCache('sr')
  return !!ss?.success && ss.value !== null && !!sr?.success && sr.value !== null && sr.value !== undefined
}

function hasDlubalCACacheFetchFailed(type: DlubalCADataType): boolean {
  return readCacheEntry(type)?.success === false
}

function needsDlubalCASnowAutoFetch(inputOptions: MountingInputsIntegrationData): boolean {
  if (hasSnowZoneInputs(inputOptions)) return false
  if (hasResolvedSnowZonesCache()) return false
  if (hasDlubalCACacheFetchFailed('snow')) return false
  return true
}

function needsDlubalCAWindAutoFetch(inputOptions: MountingInputsIntegrationData): boolean {
  if (hasValidValue(inputOptions.windSpeed)) return false
  if (hasResolvedWindCache()) return false
  if (hasDlubalCACacheFetchFailed('wind')) return false
  return true
}

const ongoingRequests = new Map<string, Promise<void>>()
let scheduledDlubalCAAutoFetch: Promise<void> | null = null
let dlubalCAAutoFetchReserved = false

function getDlubalCARequestKey(lat: unknown, lon: unknown): string {
  return `${Number(lat)}-${Number(lon)}`
}

function applyCombinedDlubalResult(result: Record<string, unknown>): void {
  const snowHasData =
    result.snow_result !== undefined && result.snow_result !== null
  const windHasData =
    result.wind_result !== undefined && result.wind_result !== null

  if (snowHasData) {
    const snow_zone_1 = result.snow_zone_1 as number | undefined
    const snow_zone_2 = result.snow_zone_2 as number | undefined
    const snow_result = result.snow_result as number
    const Ss = snow_zone_1 ?? snow_result
    const Sr = snow_zone_2 ?? 0
    setDlubalCACache('snow', {
      schema_version: DLUBAL_CA_SCHEMA_VERSION,
      snow_result,
      snow_zone_1,
      snow_zone_2,
      snow_design_kPa: computeFastRackDesignSnow_kPa(Ss, Sr, 20),
      success: true,
    })
  } else {
    setDlubalCACacheFailure('snow')
  }

  if (windHasData) {
    setDlubalCACache('wind', {
      schema_version: DLUBAL_CA_SCHEMA_VERSION,
      wind_result: result.wind_result as number,
      wind_zone_2: result.wind_zone_2 as number | undefined,
      success: true,
    })
  } else {
    setDlubalCACacheFailure('wind')
  }
}

/**
 * Fetch both snow and wind from the combined CA Dlubal endpoint in a
 * single HTTP call. Each result is cached independently so partial
 * success is handled (e.g. snow available but wind unavailable).
 */
export async function fetchDlubalCAData(): Promise<void> {
  if (!window.projectForm) return

  const snowCache = readCacheEntry('snow')
  const windCache = readCacheEntry('wind')
  if (snowCache?.success === true && windCache?.success === true) return

  const { lat, lon } = window.projectForm.getState().values
  if (!isValidDlubalCAProjectCoordinates(lat, lon)) {
    setDlubalCACacheFailure('snow')
    setDlubalCACacheFailure('wind')
    throw new DlubalCAMissingLocationError()
  }

  const requestKey = getDlubalCARequestKey(lat, lon)
  if (ongoingRequests.has(requestKey)) {
    return ongoingRequests.get(requestKey)
  }

  const requestPromise = (async () => {
    try {
      const client = restClient(window.API_ROOT + '/api')
      const url = composeUrlWithQueryParams({
        baseUrl: 'structural/get_dlubal_ca_loads',
        params: { lat, lon },
      })

      const res = await client('CUSTOM_GET', 'loadDlubalCAData', { url })
      if (!res.data?.success) {
        trackDlubalCACoverage(res.data?.coverage, false)
        throw Error('CA Dlubal loads fetch failed')
      }

      applyCombinedDlubalResult(res.data.result ?? {})
      trackDlubalCACoverage(res.data.coverage, true)
    } catch (err) {
      if (!isDlubalCAMissingLocationError(err)) {
        trackDlubalCACoverage(undefined, false)
        Sentry.captureException(err instanceof Error ? err : new Error('CA Dlubal loads fetch failed'), {
          tags: {
            source: 'fast_rack_dlubal',
            component: 'mounting',
            integration: 'fastRack',
          },
        })
        setDlubalCACacheFailure('snow')
        setDlubalCACacheFailure('wind')
      }
    } finally {
      ongoingRequests.delete(requestKey)
    }
  })()

  ongoingRequests.set(requestKey, requestPromise)
  return requestPromise
}

const hasValidValue = (value: unknown): boolean => {
  return value !== undefined && value !== null && String(value).trim() !== ''
}

function hasSnowZoneInputs(inputOptions: MountingInputsIntegrationData): boolean {
  return hasValidValue(inputOptions.snowSs_kPa) && hasValidValue(inputOptions.snowSr_kPa)
}

function hasResolvedWindCache(): boolean {
  const entry = readCacheEntry('wind')
  return !!entry?.success && resolveWindFromCache(entry) !== null
}

function getDlubalCAAutoFetchAttempts(): number {
  if (!window.projectForm) return 0
  const customData = window.projectForm.getState().values.custom_data || {}
  const meta = customData[DLUBAL_CA_AUTO_FETCH_META_KEY] as DlubalCAAutoFetchMeta | undefined
  return typeof meta?.attempts === 'number' && meta.attempts > 0 ? meta.attempts : 0
}

function setDlubalCAAutoFetchAttempts(attempts: number): void {
  if (!window.projectForm) return
  const customData = window.projectForm.getState().values.custom_data || {}
  window.projectForm.change('custom_data', {
    ...customData,
    [DLUBAL_CA_AUTO_FETCH_META_KEY]: { attempts } satisfies DlubalCAAutoFetchMeta,
  })
}

function hasExceededDlubalCAAutoFetchAttempts(): boolean {
  return getDlubalCAAutoFetchAttempts() >= DLUBAL_CA_MAX_AUTO_FETCH_ATTEMPTS
}

function tryReserveDlubalCAAutoFetch(): boolean {
  if (dlubalCAAutoFetchReserved || hasExceededDlubalCAAutoFetchAttempts()) {
    return false
  }
  dlubalCAAutoFetchReserved = true
  setDlubalCAAutoFetchAttempts(DLUBAL_CA_MAX_AUTO_FETCH_ATTEMPTS)
  return true
}

function clearDlubalCAAutoFetchReservation(): void {
  dlubalCAAutoFetchReserved = false
  setDlubalCAAutoFetchAttempts(0)
}

async function executeDlubalCAAutoFetch(needsSnow: boolean, needsWind: boolean): Promise<void> {
  if (!window.projectForm) return

  const { lat, lon } = window.projectForm.getState().values
  if (!isValidDlubalCAProjectCoordinates(lat, lon)) {
    setDlubalCACacheFailure('snow')
    setDlubalCACacheFailure('wind')
    return
  }

  const snowRequestId = needsSnow ? generateUUID() : null
  const windRequestId = needsWind ? generateUUID() : null

  if (needsSnow) {
    window.reduxStore?.dispatch({
      type: 'MOUNTING_AUTO_FETCH_START',
      dataKey: 'dlubal_ca_snow',
      requestId: snowRequestId,
    })
  }
  if (needsWind) {
    window.reduxStore?.dispatch({
      type: 'MOUNTING_AUTO_FETCH_START',
      dataKey: 'dlubal_ca_wind',
      requestId: windRequestId,
    })
  }

  try {
    await fetchDlubalCAData()
  } catch (error) {
    if (!isDlubalCAMissingLocationError(error)) {
      Sentry.captureException(
        error instanceof Error ? error : new Error('CA Dlubal auto-fetch failed'),
        {
          tags: {
            source: 'fast_rack_dlubal',
            component: 'mounting',
            integration: 'fastRack',
          },
        }
      )
    }
  }

  // Let mounting fields process START (they defer handler work with setTimeout(0))
  // before COMPLETE clears spinners — avoids a phantom 20s countdown toast.
  await new Promise<void>((resolve) => setTimeout(resolve, 0))

  if (needsSnow) {
    const ssCache = getDlubalCASnowZoneCache('ss')
    const srCache = getDlubalCASnowZoneCache('sr')
    window.reduxStore?.dispatch({
      type: 'MOUNTING_AUTO_FETCH_COMPLETE',
      dataKey: 'dlubal_ca_snow',
      resultKey: 'snow_zone_1',
      requestId: snowRequestId,
      success: !!ssCache?.success && ssCache.value !== null,
      cachedData: ssCache?.value,
    })
    window.reduxStore?.dispatch({
      type: 'MOUNTING_AUTO_FETCH_COMPLETE',
      dataKey: 'dlubal_ca_snow',
      resultKey: 'snow_zone_2',
      requestId: snowRequestId,
      success: !!srCache?.success && srCache.value !== null && srCache.value !== undefined,
      cachedData: srCache?.value,
    })
  }

  if (needsWind) {
    const windCache = getDlubalCACache('wind')
    window.reduxStore?.dispatch({
      type: 'MOUNTING_AUTO_FETCH_COMPLETE',
      dataKey: 'dlubal_ca_wind',
      resultKey: 'wind_zone_2',
      requestId: windRequestId,
      success: !!windCache?.success && windCache.value !== null,
      cachedData: windCache?.value,
    })
  }

  if ((!needsSnow || hasResolvedSnowZonesCache()) && (!needsWind || hasResolvedWindCache())) {
    clearDlubalCAAutoFetchReservation()
  }
}

/**
 * Auto-fetch snow and wind from Dlubal CA if not already cached and user
 * has not manually entered values. Dispatches Redux events so the input
 * fields show loading spinners (same pattern as K2Dlubal).
 */
export async function autoFetchDlubalCADataIfNeeded(inputOptions: MountingInputsIntegrationData): Promise<void> {
  const needsSnow = needsDlubalCASnowAutoFetch(inputOptions)
  const needsWind = needsDlubalCAWindAutoFetch(inputOptions)

  if (!needsSnow && !needsWind) return

  if (!window.projectForm) return

  if (scheduledDlubalCAAutoFetch) {
    return scheduledDlubalCAAutoFetch
  }

  if (!tryReserveDlubalCAAutoFetch()) return

  scheduledDlubalCAAutoFetch = executeDlubalCAAutoFetch(needsSnow, needsWind).finally(() => {
    scheduledDlubalCAAutoFetch = null
  })

  return scheduledDlubalCAAutoFetch
}

/** @internal Resets module-level auto-fetch guards between unit tests. */
export function resetDlubalCAAutoFetchStateForTests(): void {
  scheduledDlubalCAAutoFetch = null
  dlubalCAAutoFetchReserved = false
}

/**
 * Read resolved Dlubal CA snow (design S kPa) and wind (q1/50 kPa).
 * Returns null for either field if fetch has not completed or failed.
 */
export function getDlubalCAValues(slopeDeg = 20): DlubalCAResolvedValues {
  const snow = getDlubalCACache('snow', slopeDeg)
  const wind = getDlubalCACache('wind', slopeDeg)
  return {
    snow_kPa: snow?.success ? snow.value : null,
    wind_q150_kPa: wind?.success ? wind.value : null,
  }
}
